<template>
  <div :class="$style.homeContainer">
    <a
      href="#main-content"
      :class="$style.skipToContent"
      >Skip to main content</a
    >
    <LeftNav />
    <div :class="$style.workspace">
      <TrialBanner />
      <div
        v-if="hasProductSurface"
        :class="$style.gradientCanvas"
        data-testid="gradient-canvas"
      >
        <div
          :class="$style.contentSurface"
          data-testid="content-surface"
        >
          <main
            id="main-content"
            ref="mainContent"
            :class="$style.homeBodyInner"
            tabindex="-1"
          >
            <div
              :class="$style.pageFrame"
              data-testid="page-frame"
            >
              <slot />
            </div>
          </main>
        </div>
      </div>
      <main
        v-else
        id="main-content"
        ref="mainContent"
        :class="$style.homeBodyInner"
        tabindex="-1"
      >
        <slot />
      </main>
    </div>
    <GlobalModalContainer />
  </div>
</template>

<script setup lang="ts">
  import { useAuthenticatedLoad } from '~/composables/useAuthenticatedLoad'
  import { useAppSettingsStore } from '~/stores/appSettingsStore'

  const appSettingsStore = useAppSettingsStore()
  const { load } = useAuthenticatedLoad()
  const route = useRoute()
  const hasProductSurface = computed(
    () => route.meta.inProductSurface !== false,
  )
  const mainContent = useTemplateRef<HTMLElement>('mainContent')

  const resetContentScroll = () => {
    if (!mainContent.value) return
    mainContent.value.scrollTop = 0
    mainContent.value.scrollLeft = 0
  }

  const removePageFinishHook = useNuxtApp().hook(
    'page:finish',
    resetContentScroll,
  )

  onUnmounted(removePageFinishHook)

  onMounted(async () => {
    if (import.meta.client) {
      await load(
        [() => appSettingsStore.getAppSettings()],
        'Could not load your workspace. Please refresh to try again.',
      )
    }
  })
</script>

<style module>
  .skipToContent {
    position: absolute;
    left: -9999px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;
    z-index: 1000;
  }

  .skipToContent:focus {
    position: fixed;
    top: 10px;
    left: 10px;
    width: auto;
    height: auto;
    padding: var(--space-8) var(--space-16);
    background: var(--color-text-primary);
    color: var(--color-text-on-brand);
    border-radius: var(--radius-sm);
    text-decoration: none;
  }

  .homeContainer {
    display: flex;
    flex-direction: row;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
  }

  .workspace {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
  }

  .gradientCanvas {
    display: flex;
    flex: 1;
    min-width: 0;
    min-height: 0;
    padding: var(--product-shell-padding);
    background: var(--gradient-background-page);
    overflow: hidden;
  }

  .contentSurface {
    --product-shell-padding: var(--product-surface-padding);

    display: flex;
    flex: 1;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    background: var(--color-surface-default);
    border-radius: var(--radius-md);
  }

  .homeBodyInner {
    flex: 1;
    min-width: 0;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .pageFrame:has([data-fixed-flow]) {
    height: 100%;
    min-height: 0;
  }

  .pageFrame {
    box-sizing: border-box;
    width: 100%;
    max-width: var(--content-max-width-wide);
    min-height: 100%;
    margin-inline: auto;
  }
</style>
